import React, { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AuthUser,
  LoginResponse,
  TotpSetupResponse,
  TotpConfirmResponse,
  TotpVerifyResponse,
  RecoveryVerifyResponse,
} from '@uniticket/shared';
import { toast } from 'sonner';
import {
  apiFetch,
  fetchRetrying429,
  setAccessToken,
  ApiError,
  pinSessionUser,
  getPinnedSessionUser,
  clearPinnedSessionUser,
  setSessionConflictHandler,
  setSessionExpiredHandler,
} from '@/lib/api-client';

// ── Auth State Machine ────────────────────────────────────────

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'totp_setup'; tempToken: string }
  | { status: 'totp_verify'; tempToken: string }
  | { status: 'password_expired'; tempToken: string }
  | { status: 'authenticated'; user: AuthUser };

interface AuthContextType {
  state: AuthState;
  user: AuthUser | null;
  isAdmin: boolean;
  isInternal: boolean;
  isExternal: boolean;
  /** May this user see Unifiber's passive network (Cocon) on maps? Server-derived — see AuthUser. */
  canViewNetwork: boolean;
  login: (email: string, password: string) => Promise<void>;
  totpSetup: () => Promise<TotpSetupResponse>;
  totpConfirm: (code: string, secret: string) => Promise<string[]>;
  totpVerify: (code: string, rememberDevice?: boolean) => Promise<void>;
  recoveryVerify: (code: string, rememberDevice?: boolean) => Promise<number>;
  logout: () => Promise<void>;
  updateUser: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// ── Helper: fetch with temp token ─────────────────────────────

async function tempFetch<T>(path: string, tempToken: string, body?: unknown): Promise<T> {
  const res = await fetch(`/internal/v1${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tempToken}`,
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, data.message || res.statusText);
  }

  return res.json() as Promise<T>;
}

// ── AuthProvider ──────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  // Mirror of state.status readable from module-level callbacks (api-client
  // handlers, logout) without a render in between. Set eagerly to 'unauthenticated'
  // wherever we leave the session on purpose, so a 401 that lands afterwards (an
  // in-flight poll, parallel calls) is ignored: no toast after a deliberate
  // sign-out, one toast for N parallel 401s, never both a conflict and an expiry.
  const statusRef = useRef<AuthState['status']>('loading');
  useEffect(() => {
    statusRef.current = state.status;
  }, [state.status]);

  // Derive user from state
  const user = state.status === 'authenticated' ? state.user : null;

  // Sync i18n language with user preference on login / session restore
  const { i18n } = useTranslation();
  useEffect(() => {
    if (user?.languagePref) {
      i18n.changeLanguage(user.languagePref);
    }
  }, [user?.languagePref, i18n]);

  // Drop this tab to a re-login state when its pinned identity no longer matches
  // the shared refresh cookie (another tab logged in as a different user). The
  // pin is intentionally NOT cleared here, so a subsequent reload keeps refusing
  // the foreign identity until the user explicitly signs in again. Depends only on
  // the stable i18n singleton so the restore/registration effects stay run-once.
  const handleSessionConflict = useCallback(() => {
    if (statusRef.current === 'unauthenticated') return; // already left — no second toast
    statusRef.current = 'unauthenticated';
    setAccessToken(null);
    setState({ status: 'unauthenticated' });
    toast.error(
      i18n.t('auth:login.sessionChanged', {
        defaultValue: 'Your session changed in another tab or window. Please sign in again.',
      }),
    );
  }, [i18n]);

  // Drop this tab to /login when a request 401'd and the shared refresh cookie no
  // longer yields a session (expired, revoked by logout / password change in another
  // tab, user deactivated). Only from 'authenticated': during 'loading' tryRestore's
  // own catch handles it, and after a deliberate logout/conflict the ref is already
  // 'unauthenticated'. The pin is kept, as in the conflict handler: if another tab
  // signs in as someone else while this tab sits on /login, a reload must refuse that
  // identity rather than adopt it. An explicit sign-in re-pins.
  const handleSessionExpired = useCallback(() => {
    if (statusRef.current !== 'authenticated') return;
    statusRef.current = 'unauthenticated';
    setAccessToken(null);
    setState({ status: 'unauthenticated' });
    toast.error(
      i18n.t('auth:login.sessionExpired', {
        defaultValue: 'Your session has expired. Please sign in again.',
      }),
    );
  }, [i18n]);

  useEffect(() => {
    setSessionConflictHandler(handleSessionConflict);
    setSessionExpiredHandler(handleSessionExpired);
    return () => {
      setSessionConflictHandler(null);
      setSessionExpiredHandler(null);
    };
  }, [handleSessionConflict, handleSessionExpired]);

  // On mount: attempt silent refresh to restore session
  useEffect(() => {
    let cancelled = false;

    async function tryRestore() {
      try {
        // A transient 429 (or any non-401 failure) must not be read as "signed out"
        // without at least retrying; nginx no longer throttles this route, so this
        // is belt-and-braces.
        const res = await fetchRetrying429('/internal/v1/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });

        if (!res.ok) {
          // 401 = no usable refresh cookie → genuinely signed out. Anything else
          // (429 after retries, 5xx) also lands on /login — a reload retries.
          if (res.status !== 401) console.warn(`Session restore failed: HTTP ${res.status}`);
          if (!cancelled) setState({ status: 'unauthenticated' });
          return;
        }

        const { accessToken } = await res.json();
        setAccessToken(accessToken);

        // Fetch user info
        const { user: authUser } = await apiFetch<{ user: AuthUser }>('/auth/me');
        if (cancelled) return;

        // Identity guard: if this tab was already a different user, the shared
        // refresh cookie now points elsewhere — refuse to silently adopt it.
        const pinned = getPinnedSessionUser();
        if (pinned && pinned !== authUser.id) {
          setAccessToken(null);
          handleSessionConflict();
          return;
        }

        pinSessionUser(authUser.id);
        setState({ status: 'authenticated', user: authUser });
      } catch (err) {
        // Includes an ApiError(429) from /auth/me after retries — same outcome.
        if (err instanceof ApiError && err.status !== 401) {
          console.warn(`Session restore failed: HTTP ${err.status}`);
        }
        if (!cancelled) setState({ status: 'unauthenticated' });
      }
    }

    tryRestore();
    return () => { cancelled = true; };
  }, [handleSessionConflict]);

  // ── login ─────────────────────────────────────────────────

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch('/internal/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({ message: res.statusText }));
      throw new ApiError(res.status, data.message || res.statusText);
    }

    const data: LoginResponse = await res.json();

    if (data.step === 'AUTHENTICATED') {
      pinSessionUser(data.user.id);
      setAccessToken(data.accessToken);
      setState({ status: 'authenticated', user: data.user });
    } else if (data.step === 'PASSWORD_EXPIRED') {
      setState({ status: 'password_expired', tempToken: data.tempToken });
    } else if (data.step === 'TOTP_SETUP') {
      setState({ status: 'totp_setup', tempToken: data.tempToken });
    } else {
      setState({ status: 'totp_verify', tempToken: data.tempToken });
    }
  }, []);

  // ── totpSetup ─────────────────────────────────────────────

  const totpSetup = useCallback(async (): Promise<TotpSetupResponse> => {
    if (state.status !== 'totp_setup') throw new Error('Not in TOTP setup state');
    return tempFetch<TotpSetupResponse>('/auth/totp/setup', state.tempToken);
  }, [state]);

  // ── totpConfirm ───────────────────────────────────────────

  const totpConfirm = useCallback(async (code: string, secret: string): Promise<string[]> => {
    if (state.status !== 'totp_setup') throw new Error('Not in TOTP setup state');

    const data = await tempFetch<TotpConfirmResponse>(
      '/auth/totp/confirm',
      state.tempToken,
      { code, secret },
    );

    pinSessionUser(data.user.id);
    setAccessToken(data.accessToken);
    setState({ status: 'authenticated', user: data.user });
    return data.recoveryCodes;
  }, [state]);

  // ── totpVerify ────────────────────────────────────────────

  const totpVerify = useCallback(async (code: string, rememberDevice?: boolean) => {
    if (state.status !== 'totp_verify') throw new Error('Not in TOTP verify state');

    const data = await tempFetch<TotpVerifyResponse>(
      '/auth/totp/verify',
      state.tempToken,
      { code, rememberDevice },
    );

    pinSessionUser(data.user.id);
    setAccessToken(data.accessToken);
    setState({ status: 'authenticated', user: data.user });
  }, [state]);

  // ── recoveryVerify ────────────────────────────────────────

  const recoveryVerify = useCallback(async (code: string, rememberDevice?: boolean): Promise<number> => {
    if (state.status !== 'totp_verify') throw new Error('Not in TOTP verify state');

    const data = await tempFetch<RecoveryVerifyResponse>(
      '/auth/recovery/verify',
      state.tempToken,
      { code, rememberDevice },
    );

    pinSessionUser(data.user.id);
    setAccessToken(data.accessToken);
    setState({ status: 'authenticated', user: data.user });
    return data.remainingCodes;
  }, [state]);

  // ── updateUser (for profile page) ────────────────────────

  const updateUser = useCallback((updatedUser: AuthUser) => {
    setState({ status: 'authenticated', user: updatedUser });
  }, []);

  // ── logout ────────────────────────────────────────────────

  const logout = useCallback(async () => {
    const wasEntraOnly = user?.authType === 'ENTRA_ID';
    // Leave the session *before* the network call: a 401 landing during or after a
    // deliberate sign-out must not raise the "session expired" toast.
    statusRef.current = 'unauthenticated';
    try {
      await fetch('/internal/v1/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore — we're logging out anyway
    }
    setAccessToken(null);
    clearPinnedSessionUser();
    setState({ status: 'unauthenticated' });

    if (wasEntraOnly) {
      window.location.href = '/internal/v1/auth/sso/logout';
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        state,
        user,
        isAdmin: user?.role === 'SYSTEM_ADMIN',
        isInternal: user?.role === 'SYSTEM_ADMIN' || user?.role === 'UNIFIBER_OPERATOR',
        isExternal: user?.role === 'EXTERNAL_OPERATOR_USER',
        // Straight from the server's AuthUser — never re-derived from the role here, so a
        // deliberate grant to an external operator actually takes effect in the UI.
        canViewNetwork: user?.canViewNetwork === true,
        login,
        totpSetup,
        totpConfirm,
        totpVerify,
        recoveryVerify,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
